import { inject } from 'tsyringe'
import { containerScoped } from '~/decorators/dependency-container'
import RequestBuilder from '~/builders/http/RequestBuilder'
import {
  MarketplaceReviewReportReasons,
  ReviewFormResult,
  ReviewFormSchemaResult,
  ReviewReplyFormResult,
  ReviewReplyFormSchemaResult,
  ReviewResult,
  ReviewsResult,
  SellerReviewForm
} from '~/models/marketplace/reviews'
import VueRouter from 'vue-router'
import { toSnakeCase } from '~/utils/snake-case'
import { ActionResult } from '~/models/shared/result'
import { SchemaResult } from '~/models/classified/report'
import { OrdersResult } from '~/models/marketplace/order'

@containerScoped()
export default class MarketplaceReviewService {
  constructor(
    @inject(RequestBuilder) private requestBuilder: RequestBuilder,
    @inject(VueRouter) private router: VueRouter
  ) {}

  getReviewsForUser(
    userId: number,
    pg: number | string = 1,
    forceForSeller: boolean = false
  ): Promise<ReviewsResult> {
    const forSeller = this.router.currentRoute.name?.startsWith(
      '__account_eshop'
    )
    const url =
      forSeller || forceForSeller
        ? `/api/market/reviews/${userId}/list/`
        : `/api/market/reviews/by/${userId}/list/`
    return this.requestBuilder
      .request('get', url)
      .params({ pg })
      .send()
  }

  getRecentReviewableOrders(pg: number | string = 1): Promise<OrdersResult> {
    return this.requestBuilder
      .request('get', `/api/market/reviews/reviewable-orders/`)
      .params({ pg })
      .send()
  }

  likeReview(reviewId: number | string): Promise<ReviewResult> {
    return this.requestBuilder
      .request('post', `/api/market/reviews/${reviewId}/like/`)
      .send()
  }

  unlikeReview(reviewId: number | string): Promise<ReviewResult> {
    return this.requestBuilder
      .request('delete', `/api/market/reviews/${reviewId}/like/`)
      .send()
  }

  deleteReview(reviewId: number | string): Promise<ActionResult> {
    return this.requestBuilder
      .request('delete', `/api/market/reviews/${reviewId}/delete/`)
      .action()
      .send()
  }

  deleteReply(reviewReplyId: number | string): Promise<ActionResult> {
    return this.requestBuilder
      .request('delete', `/api/market/reviews/replies/${reviewReplyId}/delete/`)
      .action()
      .send()
  }

  getNewReviewSchema(
    sellerId: number | string
  ): Promise<ReviewFormSchemaResult> {
    return this.requestBuilder
      .request('get', `/api/market/reviews/${sellerId}/new/`)
      .send()
  }

  getNewReviewReplySchema(
    reviewId: number | string
  ): Promise<ReviewReplyFormSchemaResult> {
    return this.requestBuilder
      .request('get', `/api/market/reviews/${reviewId}/reply/`)
      .send()
  }

  getEditReviewReplySchema(
    reviewReplyId: number | string
  ): Promise<ReviewReplyFormSchemaResult> {
    return this.requestBuilder
      .request('get', `/api/market/reviews/replies/${reviewReplyId}/edit/`)
      .send()
  }

  getEditReviewValuesAndSchema(
    reviewId: number | string
  ): Promise<ReviewFormSchemaResult> {
    return this.requestBuilder
      .request('get', `/api/market/reviews/${reviewId}/edit/`)
      .send()
  }

  createNewReview(
    sellerId: number | string,
    review: SellerReviewForm
  ): Promise<ReviewFormResult> {
    return this.requestBuilder
      .request('post', `/api/market/reviews/${sellerId}/new/`)
      .data(toSnakeCase(review))
      .send()
  }

  editReview(
    reviewId: number | string,
    review: SellerReviewForm
  ): Promise<ReviewFormResult> {
    return this.requestBuilder
      .request('put', `/api/market/reviews/${reviewId}/edit/`)
      .data(toSnakeCase(review))
      .send()
  }

  sendNewReviewReply(
    reviewId: number | string,
    comment: string
  ): Promise<ReviewReplyFormResult> {
    return this.requestBuilder
      .request('post', `/api/market/reviews/${reviewId}/reply/`)
      .data({ comment })
      .send()
  }

  updateReviewReply(
    reviewReplyId: number | string,
    comment: string
  ): Promise<ReviewReplyFormResult> {
    return this.requestBuilder
      .request('put', `/api/market/reviews/replies/${reviewReplyId}/edit/`)
      .data({ comment })
      .send()
  }

  getReportSchema(reviewId: number): Promise<SchemaResult> {
    return this.requestBuilder
      .request('get', `/api/market/reviews/${reviewId}/report/`)
      .send()
  }

  reportReview(
    reviewId: number,
    {
      reason,
      details
    }: { reason?: MarketplaceReviewReportReasons; details?: string | null }
  ): Promise<ActionResult> {
    return this.requestBuilder
      .request('post', `/api/market/reviews/${reviewId}/report/`)
      .data({
        report_reason: reason || undefined,
        details: details || undefined
      })
      .send()
  }
}
